/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { jsx, css } from '@emotion/react'
import { Row, Col } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { Trans, Translation } from 'react-i18next'
import { default_theme as theme } from '../emotion/theme'
import WrappingBusyMask from './WrappingBusyMask'
import { StandaloneCheckboxField } from './form/CheckboxField'
import { InlineInputField } from './form/InlineInputField'
import { customerPhoneNumberList } from '../actions/customer_phone_number'
import { VoiceNumberGreetingSelector } from './VoiceNumberGreetingSelector'
import { answeringMessageIntroMessageList } from '../actions/answering_message'
import { Separator } from './layout/Separator'
import { BlueOutlineButton } from './layout/BlueOutlineButton'

export const VoiceCallPlanIntroMessageRow = ({customer_phone_number, canEdit, clusterType, is_loading, onUpgrade, isSaving, onSave, onToggle, isEnabled, isToggleSaving, introMessage, overrideAnsweringIntroMessageList}) => {

    const dispatch = useDispatch()
    const initialValues = { 'intro_message': introMessage }
    const hasValidMessage = Boolean(introMessage)
    const answeringMessageList = overrideAnsweringIntroMessageList || answeringMessageIntroMessageList

    useEffect(() => {
        dispatch(answeringMessageList.updateListFilter({customer: customer_phone_number.customer,
                                                        category: "intro_message"}))
    }, [])
                                                  
    const renderReadOnlyRow = () => {
        return (
            <>
              <Trans>Play welcome message on incoming call</Trans>
              {isEnabled && 
               <span css={intro_message_name_read_only_style}>
                 <>
                   { !introMessage && <Trans>Not selected</Trans> }
                   { introMessage && introMessage.name }
                 </>
               </span>
              }
            </>
        )
    }

    const renderFormFields = (formik_props) => {
        return (
            <div css={number_greeting_selector_container_style}>
              <Row>
                <Col>
                  <Trans>Play welcome message on incoming call</Trans>
                </Col>
              </Row>
              <Separator variant="h30" />
              <Row>
                <Col css={multivalue_col_style}>
                  <VoiceNumberGreetingSelector
                      formik_props={formik_props}
                      field_name="intro_message"
                      customer_phone_number={customer_phone_number}
                      showDefaultMessages={false}
                      overrideAnsweringMessageList={answeringMessageList}
                      answering_message_category="intro_message"
                  />
                </Col>
              </Row>
            </div>
        )
    }

    const onCancelEdit = () => {
        if ( ! introMessage && isEnabled ) {
            onToggle(clusterType)
        }
    }

    return (
        <div css={[row_style, white_row_style, isEnabled ? null : disabled_row_style]}>
          <div css={toggle_column_style}>
            <div css={toggle_checkbox_cell_style}>
              <StandaloneCheckboxField
                  onChange={() => onToggle(clusterType)} 
                  label=""
                  is_saving={isToggleSaving}
                  variant="large"
                  checked={isEnabled}
                  disabled={!canEdit}
              />
            </div>
          </div>
          <div css={value_column_style}>
            <div css={multivalue_col_style}>
              <InlineInputField
                  startInEditingMode={canEdit && isEnabled && !hasValidMessage}
                  formButtonStyle="buttons"
                  readOnlyValue={<div css={readonly_row_style}>{renderReadOnlyRow()}</div>}
                  formInitialValues={initialValues}
                  canEdit={canEdit && isEnabled}
                  onSave={onSave}
                  onCancel={onCancelEdit}
                  is_saving={isSaving}
                  renderFormFields={renderFormFields}
                  renderRightSide={() => {
                      if (canEdit) {
                          return null
                      } else {
                          return (
                              <>
                                <div css={upgrade_to_unlock_notification}>
                                  <Trans i18nKey={'upgrade_to_unlock_feature'}>
                                    Upgrade to a paid subscription to unlock this feature
                                  </Trans>
                                </div>
                                <BlueOutlineButton onClick={onUpgrade}>
                                  Upgrade
                                </BlueOutlineButton>
                              </>
                          )
                      }
                  }}
                  renderReadOnlyValue={({ startEditing }) => {
                      return (
                          <div css={readonly_row_style}>
                            {canEdit && <div css={readonly_row_style}>{renderReadOnlyRow()}</div>}
                            {!canEdit && (
                                <div css={disabled_row_style}>
                                  <Trans>Play welcome message on incoming call</Trans>
                                </div>
                            )}
                          </div>
                      )
                  }}
              />
            </div>
          </div>
        </div>
    )

}

const row_style = css`
  display: flex;
  flex-wrap: wrap;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 15px;
  width: 100%;
  margin-left: 0px;
  margin-right: 30px;
  align-items: center;
  min-height: 68px;
`

const white_row_style = css`
  background-color: #fff;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  align-items: center;
  line-height: 18px;
`

const disabled_row_style = css`
  color: ${theme.colors.dark_grey};
`

const toggle_column_style = css`
  display: flex;
  margin-right: 15px;
  min-width: 40px;
  max-width: 40px;
  align-self: flex-start;
  margin-top: 8px;
  justify-content: center;
  width: 100%;
`

const value_column_style = css`
  padding-bottom: 15px;
  flex-grow: 1;
`

const multivalue_col_style = css`
  display: flex;
  align-items: center;
  min-width: 1000px;
  width: 100%;
`

const toggle_checkbox_cell_style = css`
  display: flex;
  flex-grow: 1;
`

const readonly_row_style = css`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
`


const intro_message_name_read_only_style = css`
    font-weight: bold;
    margin-left: 10px;
`

const number_greeting_selector_container_style = css`
    margin-top: 10px;
    min-width: 700px;
`

const upgrade_to_unlock_notification = css`
  margin-right: 10px;
`
